import { useStore } from '../api/index.ts';
import {
  type RouteLocationNormalizedGeneric,
  type RouteLocationNormalizedLoadedGeneric,
  type NavigationGuardNext,
} from 'vue-router';
import { usePostHog } from '../composables/usePosthog.ts';
import { getAuth } from 'firebase/auth';
import { getApp } from 'firebase/app';
import { hostname } from '../assets/js/helpers/DomainUtils.ts';

async function navigationGuard(
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedLoadedGeneric,
  next: NavigationGuardNext,
) {
  const store = useStore();

  try {
    usePostHog();
  } catch (error) {
    console.error('Error initializing PostHog:', error);
  }

  // Early return if navigating to the same route or if the route is /api/health
  if (to.fullPath === from.fullPath || to.fullPath == '/api/health') {
    store.appLoading = false;
    next();
    return;
  }

  document.title = `Rack Manage${to.meta.title ? ' - ' + to.meta.title.toString() : ''}`;

  // If authorizedDomains is set and the browser still has an array, replace it with a glob string
  if (store.authorizedDomains && Array.isArray(store.authorizedDomains)) {
    await store.getAuthorizedDomains();
  }

  let continueURL: URL|null = null;

  try {
    if (to.query.continueUrl) {
      continueURL = new URL(to.query.continueUrl.toString());
    }
  } catch {
    continueURL = null;
  }

  // If continueURL and oobCode are set and we are on a different server, redirect to the continueURL
  if (continueURL && continueURL.hostname && to.query.oobCode && hostname() != continueURL.hostname) {
    let newUrl;
    try {
      newUrl = new URL(`https://${continueURL.hostname}${to.fullPath}`);
    } catch {
      newUrl = continueURL;
    }
    window.location.href = newUrl.toString();

  } else {
    store.curDomain = hostname();
    const devDomain = hostname().endsWith('.' + import.meta.env.VITE_DEV_DOMAIN);
    // Start by checking if domain exists in SaaS DB or is localhost / primary domain
    if (
      hostname() == 'localhost' ||
      hostname() == import.meta.env.VITE_HOSTNAME ||
      hostname() == 'rackmanage.net' ||
      devDomain ||
      await store.checkDomain(hostname())
    ) {
      // Update Firebase Domain (only if not already initialized)
      try {
        await store.initializeFirebase();
      } catch (error) {
        // Firebase may already be initialized, which is fine
        if (error instanceof Error && !error.message.includes('already been initialized')) {
          console.error('Firebase initialization error:', error);
        }
      }

      // The local session has already ended; the callback only completes navigation.
      if (to.path === '/auth/sso-logout') {
        const stateParam = to.query.state as string | undefined;
        const expectedState = store.ssoLogoutState;
        store.appLoading = false;
        if (expectedState != null && stateParam != null && stateParam === expectedState) {
          store.ssoLogoutState = null;
          next({ path: '/auth/login' });
        } else {
          next({ path: getAuth(getApp(store.firebaseApp)).currentUser ? '/app/dashboard' : '/auth/login' });
        }
        return;
      }

      // If user is signed out
      if (!store.user) {
        store.resetColorPreferences();
        if (store.checkedDomain?.data?.colors?.loginBackground) {
          const primaryColors = store.checkedDomain.data.colors.primary;
          const bgColors = store.checkedDomain.data.colors.loginBackground;
          const loginBackgroundString = bgColors.red + ' ' + bgColors.green + ' ' + bgColors.blue;
          const primaryString = primaryColors.red + ' ' + primaryColors.green + ' ' + primaryColors.blue;
          document.querySelector<HTMLElement>(':root')?.style.setProperty(
            '--login-background-color',
            loginBackgroundString,
          );
          document.querySelector<HTMLElement>(':root')?.style.setProperty(
            '--primary-color',
            primaryString,
          );
        }
        store.appLoading = false;
        // If user is trying to access a protected page, redirect to login
        if (to.matched.some((record) => record.meta.authRequired)) {
          next({
            path: '/auth/login',
            query: { redirect: to.fullPath },
          });
        } else {
          next();
        }
      }
      // If user is signed in
      else {
        let emailUnverified: boolean;

        if (store.user.emailVerified) {
          emailUnverified = false;
        } else {
          let containsEmailPassword = false;
          for (const providerData of store.user.providerData) {
            if (providerData.providerId == 'password') {
              containsEmailPassword = true;
            }
          }
          if (containsEmailPassword) {
            emailUnverified = true;
          } else {
            emailUnverified = false;
          }
        }

        // If user's email is unverified, log them out
        if (emailUnverified && to.matched.some((record) => record.meta.authRequired)) {
          await store.logout();
          store.resetColorPreferences();

          if (store.checkedDomain?.data?.colors?.loginBackground) {
            const primaryColors = store.checkedDomain.data.colors.primary;
            const bgColors = store.checkedDomain.data.colors.loginBackground;
            const loginBackgroundString = bgColors.red + ' ' + bgColors.green + ' ' + bgColors.blue;
            const primaryString = primaryColors.red + ' ' + primaryColors.green + ' ' + primaryColors.blue;
            document.querySelector<HTMLElement>(':root')?.style.setProperty(
              '--login-background-color',
              loginBackgroundString,
            );
            document.querySelector<HTMLElement>(':root')?.style.setProperty(
              '--primary-color',
              primaryString,
            );
          }

          store.appLoading = false;
          if (to.matched.some((record) => record.meta.authRequired)) {
            next({
              path: '/auth/login',
              query: { redirect: to.fullPath },
            });
          } else {
            next();
          }
        }
        // If the requested URL has a redirect query, redirect to that URL
        else if (to.query.redirect) {
          store.appLoading = false;
          next({
            path: to.query.redirect.toString(),
          });
        }
        // If user is trying to access a protected page
        else if (to.matched.some((record) => record.meta.authRequired)) {
          store.appLoading = false;
          const { minimatch } = await import('minimatch');
          // Check if domain is on users's authorized domains list
          if (minimatch(hostname(), store.authorizedDomains)) {
            // If user is trying to access an admin page, check if they are an admin
            if (to.matched.some((record) => record.meta.adminRequired)) {
              if (store.system.permissions && (store.system.permissions.includes('administrator') || store.system.permissions.includes('moderator'))) {
                next();
              } else {
                window.console.log('User does not have admin permissions');
                next({
                  path: '/app/dashboard',
                });
              }
            } else {
              await store.getColorPreferences();
              await store.getUserPreferences(store.user.uid);

              // Check for SSO Enforcement
              if (store.team && store.userMode == 'team' && to.path != '/app/sso') {
                const fbApp = getApp(store.firebaseApp);
                const auth = getAuth(fbApp);
                const tokenResult = await auth.currentUser?.getIdTokenResult();
                const signInProvider = tokenResult?.signInProvider ?? null;
                /* @ts-expect-error sign_in_attributes */
                const tokenTenantId = tokenResult?.claims.firebase?.sign_in_attributes?.tenant?.id ?? null;
                // Check if team matches, if token is missing, not using SSO, if SSO is enforced for this team
                // This is a client-side check to display the interstitial and will be enforced by Firestore Rules
                await store.getTeam(store.team);
                if (
                  (tokenTenantId == null || signInProvider != 'oidc.rackmanage' || tokenTenantId != store.team)
                  && store.teamData?.owner !== store.user?.uid
                  && store.teamData?.enforceSso
                  && !store.teamData?.ssoBypassUsers?.includes(store.user?.uid)
                ) {
                  next({
                    path: '/app/sso',
                  });
                } else {
                  next();
                }
              }
              // Prevent user from accessing SSO page if they are not in team mode
              else if (to.path == '/app/sso') {
                // If user is in user mode or is the team owner, redirect to dashboard
                if ((store.userMode == 'user' && !store.team) || (store.teamData?.owner == store.user?.uid) || store.teamData?.ssoBypassUsers?.includes(store.user?.uid)) {
                  next({
                    path: '/app/dashboard',
                  });
                } else {
                  const fbApp = getApp(store.firebaseApp);
                  const auth = getAuth(fbApp);
                  const tokenResult = await auth.currentUser?.getIdTokenResult();
                  const signInProvider = tokenResult?.signInProvider ?? null;
                  /* @ts-expect-error sign_in_attributes */
                  const tokenTenantId = tokenResult?.claims.firebase?.sign_in_attributes?.tenant?.id ?? null;

                  if (
                    // If user already has a valid SSO token
                    (
                      tokenTenantId == store.team
                      && store.teamData?.enforceSso
                      && store.userTeams.some((t: { id: string }) => t.id == tokenTenantId)
                    ) ||
                    // Or if SSO is not enforced and the user is not logged in with SSO
                    (!store.teamData?.enforceSso && store.team && signInProvider != 'oidc.rackmanage')) {
                    next({
                      path: '/app/dashboard',
                    });
                  } else {
                    next();
                  }
                }
              } else {
                next();
              }
            }
          } else {
            window.console.log(`User is not authorized to access ${hostname()}`);

            store.resetColorPreferences();
            await store.logout();
            window.location.href = 'https://' + import.meta.env.VITE_HOSTNAME;
          }
        }
        // If user is trying to access an auth page, redirect to dashboard
        else if (to.matched.some((record) => record.meta.toDash)) {
          await store.getColorPreferences();
          store.appLoading = false;
          next({
            path: '/app/dashboard',
          });

        }
        // If user is trying to access an unprotected page (index, pricing, etc), let them
        else {
          await store.getColorPreferences();
          store.appLoading = false;
          next();
        }

      }
    } else {
      window.location.href = 'https://' + import.meta.env.VITE_HOSTNAME;
    }
  }
}

export default navigationGuard;
